/**
 * Wager / challenge records in Firestore — shared between creator and opponent.
 */
import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { friendshipDocumentId } from '../privateChat';

export const CHALLENGES_COLLECTION = 'challenges';

export const ChallengeStatus = {
  PENDING_ACCEPTANCE: 'PENDING_ACCEPTANCE',
  PENDING_AGREEMENT: 'PENDING_AGREEMENT',
  ACTIVE: 'ACTIVE',
  LOCKED_IN: 'LOCKED_IN',
  PENDING_WINNER: 'PENDING_WINNER',
  SECOND_CHANCE: 'SECOND_CHANCE',
  DISPUTED: 'DISPUTED',
  REFUNDED: 'REFUNDED',
  DECLINED: 'DECLINED',
  SETTLED: 'SETTLED',
};

function mapDoc(id, data) {
  return {
    id,
    ...data,
    createdAt: data.createdAt?.toMillis?.() ?? data.createdAt ?? null,
    updatedAt: data.updatedAt?.toMillis?.() ?? data.updatedAt ?? null,
    expiresAt: data.expiresAt?.toMillis?.() ?? data.expiresAt ?? null,
  };
}

/**
 * Real-time list of all challenges the user is part of (creator or recipient).
 * @param {string} userId
 * @param {(bets: object[]) => void} onChallenges
 * @param {(err: Error) => void} [onError]
 */
export function subscribeUserChallenges(userId, onChallenges, onError) {
  if (!userId) {
    onChallenges([]);
    return () => {};
  }
  const col = collection(db, CHALLENGES_COLLECTION);
  const q = query(col, where('participantIds', 'array-contains', userId));

  return onSnapshot(
    q,
    (snap) => {
      const bets = snap.docs
        .map((d) => mapDoc(d.id, d.data()))
        .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
      onChallenges(bets);
    },
    (err) => {
      console.error('subscribeUserChallenges:', err);
      onError?.(err);
      onChallenges([]);
    }
  );
}

/**
 * Pre-generate a challenges doc ref — use its id for wallet matchId before setDoc.
 * Same pattern as Open Arena accept (openChallengeService.acceptOpenChallengeTransaction).
 */
export function buildChallengeRef() {
  return doc(collection(db, CHALLENGES_COLLECTION));
}

async function assertWagerFriendshipAllowed(creatorId, recipientId) {
  const friendshipId = friendshipDocumentId(creatorId, recipientId);
  if (!friendshipId) throw new Error('Invalid players for challenge');
  const snap = await getDoc(doc(db, 'friendships', friendshipId));
  if (!snap.exists()) throw new Error('NOT_FRIENDS');
  const data = snap.data();
  const userIds = data.userIds || [];
  if (!userIds.includes(creatorId) || !userIds.includes(recipientId)) {
    throw new Error('NOT_FRIENDS');
  }
  const blocked = data.blockedBy || {};
  if (blocked[creatorId] === true || blocked[recipientId] === true) {
    throw new Error('WAGER_BLOCKED');
  }
}

/**
 * @param {object} bet — same shape as local mock bet (id optional; preset ref wins)
 * @param {import('firebase/firestore').DocumentReference} [presetRef]
 * @returns {Promise<string>} document id
 */
export async function createChallenge(bet, presetRef = null) {
  const creatorId = bet.creatorId;
  const recipientId = bet.recipientId;
  if (!creatorId || !recipientId) throw new Error('creatorId and recipientId required');

  await assertWagerFriendshipAllowed(creatorId, recipientId);

  const participantIds = [creatorId, recipientId].sort();
  const payload = {
    creatorId,
    recipientId,
    participantIds,
    status: bet.status,
    totalPot: bet.totalPot ?? 0,
    creatorRiskTally: bet.creatorRiskTally ?? 0,
    recipientRiskTally: bet.recipientRiskTally ?? 0,
    participants: bet.participants ?? [],
    matchup: bet.matchup ?? {},
    agreedBy: bet.agreedBy ?? [],
    waitingForAmountApprovalFromId: bet.waitingForAmountApprovalFromId ?? null,
    winnerVotes: bet.winnerVotes ?? null,
    secondChanceEndsAt: bet.secondChanceEndsAt ?? null,
    disputeEndsAt: bet.disputeEndsAt ?? null,
    challengeType: bet.challengeType ?? (bet.matchup?.isCustom ? 'custom' : 'live'),
    recipientNotified: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  // Firestore rules reject creates that include a `settlement` key (even null).
  if (bet.finalOutcome != null) payload.finalOutcome = bet.finalOutcome;
  if (bet.settlement != null) payload.settlement = bet.settlement;

  const ref = presetRef ?? doc(collection(db, CHALLENGES_COLLECTION));
  await setDoc(ref, payload);
  return ref.id;
}

export async function updateChallenge(challengeId, updates) {
  if (!challengeId) return;
  await updateDoc(doc(db, CHALLENGES_COLLECTION, challengeId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function acceptChallenge(challengeId) {
  await updateChallenge(challengeId, { status: ChallengeStatus.ACTIVE });
}

export async function declineChallenge(challengeId, declinedByUserId) {
  await updateChallenge(challengeId, {
    status: ChallengeStatus.DECLINED,
    declinedByUserId: declinedByUserId ?? null,
  });
}

export async function agreeCustomChallenge(challengeId, userId, existingAgreedBy = []) {
  const agreedBy = [...existingAgreedBy, userId].filter((id, i, arr) => arr.indexOf(id) === i);
  const bothAgreed = agreedBy.length >= 2;
  await updateChallenge(challengeId, {
    agreedBy,
    status: bothAgreed ? ChallengeStatus.LOCKED_IN : ChallengeStatus.PENDING_AGREEMENT,
  });
}

export async function counterChallenge(challengeId, recipientUserId, amount, participants) {
  await updateChallenge(challengeId, {
    waitingForAmountApprovalFromId: recipientUserId,
    participants,
    totalPot: amount * 2,
  });
}

export async function removeChallenge(challengeId) {
  if (!challengeId) return;
  await deleteDoc(doc(db, CHALLENGES_COLLECTION, challengeId));
}

/** Persist outcome so both players see the same winner notification. */
export async function recordChallengeFinalOutcome(challengeId, finalOutcome, matchupPatch) {
  if (!challengeId) return;
  const updates = { finalOutcome, updatedAt: serverTimestamp() };
  if (matchupPatch) updates.matchup = matchupPatch;
  await updateDoc(doc(db, CHALLENGES_COLLECTION, challengeId), updates);
}

export async function recordChallengeSettlement(challengeId, settlement) {
  if (!challengeId) return;
  await updateChallenge(challengeId, { settlement });
}
